import { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import CreateApiKeyDialog from "../components/CreateApiKeyDialog";

export default function ApiKeys() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [recentlyCreatedKey, setRecentlyCreatedKey] = useState<string | null>(
    null,
  );

  const handleApiKeyCreated = (apiKey: string) => {
    setRecentlyCreatedKey(apiKey);
    // Clear the recent key after 30 seconds for security
    setTimeout(() => setRecentlyCreatedKey(null), 30000);
  };

  return (
    <AdminLayout
      title="API Keys"
      description="Manage API keys for programmatic access to Amplifiles"
      breadcrumb="API Keys"
      headerAction={
        <button
          onClick={() => setIsCreateDialogOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create API Key
        </button>
      }
    >
      {/* Recently Created Key Alert */}
      {recentlyCreatedKey && (
        <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-400 rounded-r-lg">
          <div className="flex">
            <div className="text-green-400 text-xl mr-3">✅</div>
            <div className="flex-1">
              <p className="text-green-700 font-medium">
                API Key Created Successfully!
              </p>
              <p className="text-green-600 text-sm mt-1">
                Your new API key has been created. Make sure you've saved it
                securely.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Active API Keys
            </h2>
            <span className="text-sm text-gray-500">
              API keys allow programmatic access to Amplifiles
            </span>
          </div>

          {/* Info Section */}
          <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
            <div className="flex">
              <div className="text-blue-400 text-xl mr-3">ℹ️</div>
              <div>
                <p className="text-blue-700 font-medium">About API Keys</p>
                <p className="text-blue-600 text-sm mt-1">
                  API keys are used to authenticate requests to the Amplifiles
                  API. Include your API key in the{" "}
                  <code className="bg-blue-100 px-1 rounded">X-API-Key</code>{" "}
                  header when making requests.
                </p>
              </div>
            </div>
          </div>

          {/* API Key List Placeholder */}
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔑</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              API Key Management
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Create API keys to access Amplifiles programmatically. This demo
              shows the create functionality.
            </p>
            <button
              onClick={() => setIsCreateDialogOpen(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Your First API Key
            </button>
          </div>

          {/* Future: API Key Table */}
          {/* This would show existing API keys with creation dates, names, and delete actions */}
        </div>
      </div>

      {/* Usage Example */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Usage Example
        </h3>
        <div className="bg-gray-900 text-white p-4 rounded-lg overflow-x-auto">
          <pre className="text-sm">
            {`curl -X POST https://api.amplifiles.ai/user/api-key \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "My API Key"}'`}
          </pre>
        </div>
      </div>

      {/* Create API Key Dialog */}
      <CreateApiKeyDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={handleApiKeyCreated}
      />
    </AdminLayout>
  );
}
